<template>
  <v-bottom-sheet
    v-model="internalVisible"
    fullscreen
    @update:model-value="handleVisibilityChange"
  >
    <div class="mobile-filter-sheet">
      <!-- Header -->
      <div class="sheet-header">
        <h3 class="header-title">Filters</h3>
        <button
          type="button"
          class="close-button"
          aria-label="Close filters"
          @click="closeSheet"
        >
          <img src="/images/close.svg" alt="Close" />
        </button>
      </div>

      <!-- Scrollable Content -->
      <div class="sheet-content filter-navigation">
        <!-- Grades -->
        <div class="filter-section">
          <button
            type="button"
            class="section-header"
            :aria-expanded="expandedSections.grades"
            aria-controls="section-grades"
            @click="toggleSection('grades')"
          >
            <span class="section-title">Grade</span>
            <div
              class="section-arrow"
              :class="{ expanded: expandedSections.grades }"
              aria-hidden="true"
            >
              <ChevronDownIcon color="#e00069" />
            </div>
          </button>

          <div
            id="section-grades"
            v-if="expandedSections.grades"
            class="section-content"
          >
            <div class="option-item" @click="toggleAll('grades')">
              <span class="option-label">All Grades</span>
              <label class="checkbox-container">
                <input
                  type="checkbox"
                  role="checkbox"
                  :checked="isAllSelected('grades')"
                  @change="toggleAll('grades')"
                  class="filter-checkbox"
                  aria-label="All Grades"
                />
                <span class="checkbox-visual" aria-hidden="true"></span>
              </label>
            </div>
            <div
              v-for="option in grades"
              :key="option.value"
              class="option-item"
              @click="onOptionRowClick('grades', option.value, $event)"
            >
              <span class="option-label">{{ option.text }}</span>
              <label class="checkbox-container">
                <input
                  type="checkbox"
                  role="checkbox"
                  :value="option.value"
                  v-model="localSelectedGrades"
                  class="filter-checkbox"
                  :aria-label="option.text"
                />
                <span class="checkbox-visual" aria-hidden="true"></span>
              </label>
            </div>
          </div>
        </div>

        <div class="section-divider"></div>

        <!-- Subjects -->
        <div class="filter-section">
          <button
            type="button"
            class="section-header"
            :aria-expanded="expandedSections.subjects"
            aria-controls="section-subjects"
            @click="toggleSection('subjects')"
          >
            <span class="section-title">Subject</span>
            <div
              class="section-arrow"
              :class="{ expanded: expandedSections.subjects }"
              aria-hidden="true"
            >
              <ChevronDownIcon color="#e00069" />
            </div>
          </button>

          <div
            id="section-subjects"
            v-if="expandedSections.subjects"
            class="section-content"
          >
            <div class="option-item" @click="toggleAll('subjects')">
              <span class="option-label">All Subjects</span>
              <label class="checkbox-container">
                <input
                  type="checkbox"
                  role="checkbox"
                  :checked="isAllSelected('subjects')"
                  @change="toggleAll('subjects')"
                  class="filter-checkbox"
                  aria-label="All Subjects"
                />
                <span class="checkbox-visual" aria-hidden="true"></span>
              </label>
            </div>
            <div
              v-for="option in subjects"
              :key="option.value"
              class="option-item"
              @click="onOptionRowClick('subjects', option.value, $event)"
            >
              <span class="option-label">{{ option.text }}</span>
              <label class="checkbox-container">
                <input
                  type="checkbox"
                  role="checkbox"
                  :value="option.value"
                  v-model="localSelectedSubjects"
                  class="filter-checkbox"
                  :aria-label="option.text"
                />
                <span class="checkbox-visual" aria-hidden="true"></span>
              </label>
            </div>
          </div>
        </div>

        <div class="section-divider"></div>

        <!-- Tech Domain -->
        <div class="filter-section">
          <button
            type="button"
            class="section-header"
            :aria-expanded="expandedSections.domains"
            aria-controls="section-domains"
            @click="toggleSection('domains')"
          >
            <span class="section-title">Tech Domain</span>
            <div
              class="section-arrow"
              :class="{ expanded: expandedSections.domains }"
              aria-hidden="true"
            >
              <ChevronDownIcon color="#e00069" />
            </div>
          </button>

          <div
            id="section-domains"
            v-if="expandedSections.domains"
            class="section-content"
          >
            <div class="option-item" @click="toggleAll('domains')">
              <span class="option-label">All Tech Domains</span>
              <label class="checkbox-container">
                <input
                  type="checkbox"
                  role="checkbox"
                  :checked="isAllSelected('domains')"
                  @change="toggleAll('domains')"
                  class="filter-checkbox"
                  aria-label="All Tech Domains"
                />
                <span class="checkbox-visual" aria-hidden="true"></span>
              </label>
            </div>
            <div
              v-for="option in domains"
              :key="option.value"
              class="option-item"
              @click="onOptionRowClick('domains', option.value, $event)"
            >
              <span class="option-label">{{ option.text }}</span>
              <label class="checkbox-container">
                <input
                  type="checkbox"
                  role="checkbox"
                  :value="option.value"
                  v-model="localSelectedDomains"
                  class="filter-checkbox"
                  :aria-label="option.text"
                />
                <span class="checkbox-visual" aria-hidden="true"></span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="sheet-footer">
        <button type="button" class="clear-button" @click="clearAllFilters">
          Clear All
        </button>
        <button type="button" class="apply-button" @click="applyFilters">
          Apply Filters
        </button>
      </div>
    </div>
  </v-bottom-sheet>
</template>

<script setup>
import { ref, watch, onMounted, defineProps, defineEmits } from "vue";
import CategoryAdminService from "@/services/categoryAdminService.js";
import ChevronDownIcon from "../icons/ChevronDownIcon.vue";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  selectedSubjects: {
    type: Array,
    default: () => [],
  },
  selectedGrades: {
    type: Array,
    default: () => [],
  },
  selectedDomains: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  "update:visible",
  "filter-change",
  "reset-filters",
  "close",
]);

const internalVisible = ref(props.visible);

const expandedSections = ref({
  subjects: true,
  grades: true,
  domains: true,
});

const subjects = ref([]);
const grades = ref([]);
const domains = ref([]);

// Local state for filters while in bottom sheet
const localSelectedSubjects = ref([]);
const localSelectedGrades = ref([]);
const localSelectedDomains = ref([]);

watch(
  () => props.visible,
  (newVal) => {
    internalVisible.value = newVal;
    if (newVal) {
      // Sync from props when opening
      localSelectedSubjects.value = [...props.selectedSubjects];
      localSelectedGrades.value = [...props.selectedGrades];
      localSelectedDomains.value = [...props.selectedDomains];
    }
  }
);

const handleVisibilityChange = (newVal) => {
  if (!newVal) {
    closeSheet();
  }
};

const fetchFilterOptions = async () => {
  try {
    const [domainRes, subjectRes, gradeRes] = await Promise.all([
      CategoryAdminService.getById(30), // Domain
      CategoryAdminService.getById(33), // Subject
      CategoryAdminService.getById(26), // Grade
    ]);

    if (domainRes.status === 200 && domainRes.data.data?.terms) {
      const filteredTerms = domainRes.data.data.terms.filter(
        (term) => term.locale !== "SG"
      );
      domains.value = filteredTerms.map((term) => ({
        text: term.title,
        value: term.title,
      }));
    }
    if (subjectRes.status === 200 && subjectRes.data.data?.terms) {
      const filteredTerms = subjectRes.data.data.terms.filter(
        (term) => term.locale !== "SG"
      );
      subjects.value = filteredTerms.map((term) => ({
        text: term.title,
        value: term.title,
      }));
    }
    if (gradeRes.status === 200 && gradeRes.data.data?.terms) {
      const filteredTerms = gradeRes.data.data.terms.filter(
        (term) => term.locale !== "SG"
      );
      grades.value = filteredTerms.map((term) => ({
        text: term.title,
        value: term.title,
      }));
    }
  } catch (error) {
    console.error("Error fetching filter options:", error);
  }
};

onMounted(async () => {
  await fetchFilterOptions();
});

const toggleSection = (section) => {
  expandedSections.value[section] = !expandedSections.value[section];
};

const isAllSelected = (filterType) => {
  if (filterType === "subjects" && subjects.value.length > 0) {
    return localSelectedSubjects.value.length === subjects.value.length;
  }
  if (filterType === "grades" && grades.value.length > 0) {
    return localSelectedGrades.value.length === grades.value.length;
  }
  if (filterType === "domains" && domains.value.length > 0) {
    return localSelectedDomains.value.length === domains.value.length;
  }
  return false;
};

const toggleAll = (filterType) => {
  const currentlyAllSelected = isAllSelected(filterType);
  if (filterType === "subjects") {
    localSelectedSubjects.value = currentlyAllSelected
      ? []
      : subjects.value.map((s) => s.value);
  } else if (filterType === "grades") {
    localSelectedGrades.value = currentlyAllSelected
      ? []
      : grades.value.map((g) => g.value);
  } else if (filterType === "domains") {
    localSelectedDomains.value = currentlyAllSelected
      ? []
      : domains.value.map((d) => d.value);
  }
};

const onOptionRowClick = (filterType, optionValue, event) => {
  if (
    event.target.type === "checkbox" ||
    event.target.closest(".checkbox-container")
  ) {
    return; // Handled by v-model
  }

  let selectedRef;
  switch (filterType) {
    case "subjects":
      selectedRef = localSelectedSubjects;
      break;
    case "grades":
      selectedRef = localSelectedGrades;
      break;
    case "domains":
      selectedRef = localSelectedDomains;
      break;
    default:
      return;
  }

  const index = selectedRef.value.indexOf(optionValue);
  if (index > -1) {
    selectedRef.value.splice(index, 1);
  } else {
    selectedRef.value.push(optionValue);
  }
};

const applyFilters = () => {
  emit("filter-change", {
    subjects: localSelectedSubjects.value,
    grades: localSelectedGrades.value,
    domains: localSelectedDomains.value,
  });
  closeSheet();
};

const clearAllFilters = () => {
  localSelectedSubjects.value = [];
  localSelectedGrades.value = [];
  localSelectedDomains.value = [];
  emit("reset-filters");
  closeSheet();
};

const closeSheet = () => {
  internalVisible.value = false;
  emit("update:visible", false);
  emit("close");
};
</script>

<style scoped>
.mobile-filter-sheet {
  display: flex;
  flex-direction: column;
  height: 100dvh;
  background: white;
  border-top-left-radius: 28px;
  border-top-right-radius: 28px;
  overflow: hidden;
}

.sheet-header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 16px;
  border-bottom: 1px solid #d0d5dd;
  flex-shrink: 0;
  background: white;
  position: sticky;
  top: 0;
  z-index: 10;
}

.header-title {
  flex: 1;
  font-family: "League Spartan", sans-serif;
  font-size: 22px;
  font-weight: 500;
  color: #003d6a;
  margin: 0;
}

.close-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
}

.close-button img {
  width: 24px;
  height: 24px;
}

.sheet-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  padding-bottom: 120px;
  -webkit-overflow-scrolling: touch;
  background: #ffffff;
}

/* Base styles adopted from PDFilterSidebar/ExpandedFilterComponent for mobile */
.filter-navigation {
  background-color: #ffffff;
}

.filter-section {
  margin-bottom: 2px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  height: 48px;
  border-radius: 12px;
  cursor: pointer;
  width: 100%;
  border: none;
  background: none;
  text-align: left;
}

.section-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: 20px;
  line-height: 28px;
  color: var(--pink-dark-1);
  letter-spacing: 0.03px;
}

.section-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  transform: rotate(180deg);
  color: #000;
}

.section-arrow.expanded {
  transform: rotate(0deg);
}

.section-arrow svg {
  width: 14px;
  height: 7px;
}

.section-content {
  padding-left: 16px;
}

.option-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  height: 48px;
  border-radius: 12px;
  cursor: pointer;
}

.option-label {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: 20px;
  line-height: 28px;
  color: var(--pink-dark-1);
  letter-spacing: 0.03px;
}

/* Checkbox styling */
.checkbox-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.filter-checkbox {
  opacity: 0;
  position: absolute;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkbox-visual {
  width: 24px;
  height: 24px;
  background-color: #ffffff;
  border: 1px solid #ccc;
  border-radius: 4px;
  position: relative;
  transition: all 0.2s ease;
}

.filter-checkbox:checked ~ .checkbox-visual,
.filter-checkbox:checked + .checkbox-visual {
  background-color: var(--pink-dark-1);
  border-color: var(--pink-dark-1);
}

.checkbox-visual:after {
  content: "";
  position: absolute;
  display: none;
}

.filter-checkbox:checked ~ .checkbox-visual:after,
.filter-checkbox:checked + .checkbox-visual:after {
  display: block;
}

.checkbox-visual:after {
  left: 7px;
  top: 3px;
  width: 6px;
  height: 10px;
  border: solid white;
  border-width: 0 3px 3px 0;
  transform: rotate(45deg);
}

.section-divider {
  height: 1px;
  background-color: #e5e7eb;
  margin: 0;
  width: 100%;
}

.sheet-footer {
  display: flex;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid #e5e7eb;
  background: white;
  flex-shrink: 0;
  position: sticky;
  bottom: 0;
  width: 100%;
}

.clear-button,
.apply-button {
  flex: 1;
  height: 40px;
  border-radius: 8px;
  font-family: "League Spartan", sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.014px;
}

.clear-button {
  background: white;
  border: 1px solid #003d6a;
  color: #003d6a;
}

.clear-icon,
.apply-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.clear-button:hover {
  background: #f2f3fb;
}

.apply-button {
  background: #003d6a;
  color: white;
}

.apply-button:hover {
  background: #002a4a;
}
</style>
